import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Flex, Grid } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import TableData from "components/ui/TableData";
import BasicButton from "components/forms/BasicButton";
import SelectInput from "components/forms/SelectInput";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { PlusIcon } from "components/svg";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";
import LeadsBulkBar from "./LeadsBulkBar";
import OrgContactsMenu from "./OrgContactsMenu";
import { getMoCounts } from "api/MaintenanceOpportunities";

import {
  getLeadsTable,
  getLeadStats,
  getLeadOptions,
  LEAD_STATUS_LABELS,
  type LeadRow,
  type LeadStats,
} from "api/Leads";

/**
 * Leads list - a WORK QUEUE, not a directory (LEADS_MODULE_SCOPE.md s.16.1).
 *
 * Sorted by next action so the top row is the thing to do now. Every row
 * states its next step, what has already gone out, and the last thing that
 * happened. Built on the shared Header/Page/TableData shell so paging,
 * search and the filter drawer behave as they do everywhere else.
 */

const columnHelper = createColumnHelper();

const createColumn = (
  id: string,
  title: string,
  accessor: (row: any) => any,
  cell?: (info: any) => any,
  options = {},
) =>
  columnHelper.accessor(accessor, {
    id,
    cell: cell || ((info) => info.getValue()),
    header: () => <span>{title}</span>,
    ...options,
  });

/**
 * Status pills use the same muted palette as the client Overview tab's
 * chips (#EAF3DE/#27500A green, #FAEEDA/#633806 amber, #E6F1FB/#185FA5
 * blue, #F1EFE8/#5f5e5a neutral) so a lead status reads like every other
 * badge in the app.
 */
export const STATUS_STYLE: Record<string, { bg: string; fg: string }> = {
  new: { bg: "#F1EFE8", fg: "#5f5e5a" },
  contacted: { bg: "#E6F1FB", fg: "#185FA5" },
  qualified: { bg: "#EAF3DE", fg: "#27500A" },
  proposal_sent: { bg: "#FAEEDA", fg: "#633806" },
  negotiating: { bg: "#EEE9F8", fg: "#4B2E83" },
  won: { bg: "#27500A", fg: "#FFFFFF" },
  lost: { bg: "#FBEAEA", fg: "#A60000" },
  dormant: { bg: "#F1EFE8", fg: "#818181" },
};

export const Pill: React.FC<{ bg: string; fg: string; children: React.ReactNode; title?: string }> = ({ bg, fg, children, title }) => (
  <span
    title={title}
    style={{
      display: "inline-block",
      padding: "2px 10px",
      background: bg,
      color: fg,
      borderRadius: 9,
      fontSize: 11,
      fontFamily: "Poppins",
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

/**
 * The next-action pill: N days overdue (red) / Today (amber) / a weekday
 * or date (plain) / none set (grey). One colour per state and no more.
 */
export const NextActionPill: React.FC<{ at: string | null }> = ({ at }) => {
  if (!at) return <Span fontFamily="Poppins" fontSize={"12px"} color={themevals.colors.dkgrey}>none set</Span>;
  const d = moment(at).startOf("day");
  const today = moment().startOf("day");
  const diff = d.diff(today, "days");
  if (diff < 0) {
    const n = -diff;
    return <Pill bg="#FBEAEA" fg="#A60000">{n} day{n === 1 ? "" : "s"} overdue</Pill>;
  }
  if (diff === 0) return <Pill bg="#FAEEDA" fg="#633806">Today</Pill>;
  if (diff < 7) return <Pill bg="#F1EFE8" fg="#5f5e5a">{d.format("ddd")}</Pill>;
  return <Pill bg="#F1EFE8" fg="#5f5e5a">{d.format("MMM D")}</Pill>;
};

const sentSummary = (r: LeadRow) => {
  const parts: string[] = [];
  if (r.rfqCount) parts.push(`${r.rfqCount} RFQ${r.rfqCount === 1 ? "" : "s"}`);
  if (r.quoteCount) parts.push(`${r.quoteCount} quote${r.quoteCount === 1 ? "" : "s"}`);
  if (r.proposalCount) parts.push(`${r.proposalCount} proposal${r.proposalCount === 1 ? "" : "s"}`);
  return parts.join(" \u00b7 ");
};

const LeadsList = () => {
  const navigate = useNavigate();
  // Entry to Leads > Maintenance opportunities, with the open count. Null
  // (no button) when the switch is off or the user lacks the permission:
  // the counts call 403s.
  const [moDue, setMoDue] = useState<number | null>(null);
  useEffect(() => {
    getMoCounts().then((c: any) => setMoDue(c?.error ? null : Number(c?.open ?? 0)));
  }, []);
  const { user } = useContext(UserContext) as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState<LeadStats | null>(null);
  // Selection for bulk actions. Keyed by id so it survives paging; the
  // rows themselves are kept for the CSV export.
  const [selected, setSelected] = useState<Record<string, LeadRow>>({});
  const [pageRows, setPageRows] = useState<LeadRow[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const selectedRows = Object.values(selected);
  const toggle = (row: LeadRow, on: boolean) =>
    setSelected((s) => {
      const next = { ...s };
      if (on) next[row.id] = row;
      else delete next[row.id];
      return next;
    });
  const allOnPage = pageRows.length > 0 && pageRows.every((r) => selected[r.id]);
  const [options, setOptions] = useState<{ owners: Array<{ id: string; name: string }>; sources: string[] }>({
    owners: [],
    sources: [],
  });
  // The owner/source filter the stats follow. Status does not narrow the
  // cards - "open leads" is a status by definition.
  const [statFilter, setStatFilter] = useState<{ owner?: string; source?: string }>({
    owner: searchParams.get("owner") ?? undefined,
    source: searchParams.get("source") ?? undefined,
  });

  const awaitingConvert = searchParams.get("awaitingConvert") === "true";
  // Sort chosen above the table; the last choice is remembered as this
  // browser's default (2026-10-16, Jack: e.g. last activity first).
  const sort = searchParams.get("sort") ?? (() => {
    try {
      return localStorage.getItem("leads.defaultSort") || "";
    } catch {
      return "";
    }
  })();
  const setSort = (v: string) => {
    try {
      if (v) localStorage.setItem("leads.defaultSort", v);
      else localStorage.removeItem("leads.defaultSort");
    } catch {
      /* storage unavailable: the choice lasts for this page only */
    }
    const next = new URLSearchParams(searchParams);
    if (v) next.set("sort", v); else next.delete("sort");
    setSearchParams(next);
  };
  // Recomputed when the "Accepted - convert" stat toggles the filter, so
  // the table (keyed on these) reloads.
  const defaultFilters = useMemo(() => {
    const f: Record<string, string> = {};
    for (const k of ["status", "owner", "source", "dueOnly", "awaitingConvert", "activity", "proposal", "quote", "state", "sort"]) {
      const v = searchParams.get(k);
      if (v) f[k] = v;
    }
    if (sort && !f.sort) f.sort = sort;
    return f;
  }, [awaitingConvert, sort]); // eslint-disable-line

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Leads";
    getLeadOptions().then(setOptions);
    void trackActivity({
      module: "leads",
      action: "view_leads_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    getLeadStats(statFilter).then((res: any) => {
      if (!res?.error) setStats(res);
    });
  }, [statFilter]);

  const tableFilters = useMemo(
    () => [
      {
        key: "status",
        component: SelectInput,
        props: {
          name: "status",
          label: "Status",
          clearable: true,
          placeholder: "All open",
          options: [
            { value: "open", label: "All open" },
            ...Object.entries(LEAD_STATUS_LABELS).map(([value, label]) => ({ value, label })),
            { value: "all", label: "Everything, including closed" },
          ],
          $customStyles: { minWidth: "180px" },
        },
      },
      {
        key: "owner",
        component: SelectInput,
        props: {
          name: "owner",
          label: "Owner",
          clearable: true,
          placeholder: "Everyone",
          options: [{ value: "mine", label: "Mine" }, ...options.owners.map((o) => ({ value: o.id, label: o.name }))],
          $customStyles: { minWidth: "180px" },
        },
      },
      {
        key: "source",
        component: SelectInput,
        props: {
          name: "source",
          label: "Source",
          clearable: true,
          placeholder: "Any",
          options: options.sources.map((s) => ({ value: s, label: s.replace(/_/g, " ") })),
          $customStyles: { minWidth: "160px" },
        },
      },
      {
        key: "activity",
        component: SelectInput,
        props: {
          name: "activity",
          label: "Last activity",
          clearable: true,
          placeholder: "Any time",
          options: [
            { value: "recent7", label: "In the last 7 days" },
            { value: "stale14", label: "Nothing in 14+ days" },
            { value: "stale30", label: "Nothing in 30+ days" },
            { value: "stale60", label: "Nothing in 60+ days" },
          ],
          $customStyles: { minWidth: "200px" },
        },
      },
      {
        key: "proposal",
        component: SelectInput,
        props: {
          name: "proposal",
          label: "Proposal",
          clearable: true,
          placeholder: "Any",
          options: [
            { value: "none", label: "No proposal yet" },
            { value: "sent_unopened", label: "Sent, not opened" },
            { value: "opened", label: "Opened" },
          ],
          $customStyles: { minWidth: "180px" },
        },
      },
      {
        key: "quote",
        component: SelectInput,
        props: {
          name: "quote",
          label: "Quote",
          clearable: true,
          placeholder: "Any",
          options: [
            { value: "none", label: "No quote yet" },
            { value: "open", label: "Quote out (not accepted)" },
            { value: "accepted", label: "Quote accepted" },
          ],
          $customStyles: { minWidth: "180px" },
        },
      },
      {
        key: "state",
        component: SelectInput,
        props: {
          name: "state",
          label: "State",
          clearable: true,
          placeholder: "Any",
          options: US_STATES.map((st) => ({ value: st, label: st })),
          $customStyles: { minWidth: "120px" },
        },
      },
      {
        key: "dueOnly",
        component: SelectInput,
        props: {
          name: "dueOnly",
          label: "Due",
          clearable: true,
          placeholder: "Everything",
          options: [{ value: "true", label: "Only with something due" }],
          $customStyles: { minWidth: "200px" },
        },
      },
    ],
    [options],
  );

  return (
    <>
      <Header />
      <Page
        title="Leads"
        subtitle="Prospects we are working. Sorted by what is due next, so the top row is the thing to do now."
        titleAction={
          <>
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate("/leads/duplicates")}>
              Duplicates
            </BasicButton>
            {moDue != null && (
              <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate("/leads/opportunities")}>
                {`Opportunities${moDue ? ` \u00b7 ${moDue} due` : ""}`}
              </BasicButton>
            )}
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate("/leads/import")}>
              Import CSV
            </BasicButton>
            <BasicButton type="button" onClick={() => navigate("/leads/new")} $iconHeight="10px">
              <PlusIcon fill="#fff" />
              New lead
            </BasicButton>
          </>
        }
      >
        <Grid gap={"16px"} columns={[2, 3, 5]} sx={{ marginBottom: "24px" }}>
          {/* Accepted a quote (online or by staff) and not converted: no
              login to pay, invoice not in Zoho. First, because it is money
              waiting (2026-10-08). Click to filter; click again to clear. */}
          <Stat
            label={awaitingConvert ? "Accepted, to convert \u00b7 showing" : "Accepted, to convert"}
            value={stats?.awaitingConvert ?? "\u2014"}
            tone={stats?.awaitingConvert ? "#A60000" : undefined}
            onClick={() => setSearchParams(awaitingConvert ? {} : { status: "all", awaitingConvert: "true" })}
          />
          <Stat label="Overdue actions" value={stats?.overdue ?? "\u2014"} tone={stats?.overdue ? "#A60000" : undefined} />
          <Stat label="Due today" value={stats?.dueToday ?? "\u2014"} tone={stats?.dueToday ? "#633806" : undefined} />
          <Stat label="Proposals out, unopened" value={stats?.unopenedProposals ?? "\u2014"} />
          <Stat label="Open leads" value={stats?.open ?? "\u2014"} />
        </Grid>

        <LeadsBulkBar
          selected={selectedRows}
          owners={options.owners}
          onClear={() => setSelected({})}
          onDone={() => {
            setSelected({});
            setTableKey((k) => k + 1);
            getLeadStats(statFilter).then((res: any) => { if (!res?.error) setStats(res); });
          }}
        />

        <Flex sx={{ justifyContent: "flex-end", alignItems: "center", gap: "8px", marginBottom: "8px", fontFamily: "Poppins", fontSize: 12, color: "#5f5e5a" }}>
          <label htmlFor="leads-sort">Sort</label>
          <select
            id="leads-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{ border: "1px solid #E4E7EB", borderRadius: 6, padding: "6px 8px", fontFamily: "Poppins", fontSize: 12, background: "#fff" }}
          >
            <option value="">Next action due (default)</option>
            <option value="lastActivityAt:desc">Last activity, newest first</option>
            <option value="lastActivityAt:asc">Last activity, oldest first</option>
            <option value="createdAt:desc">Newest leads first</option>
            <option value="name:asc">Name A-Z</option>
            <option value="leadStatus:asc">Status</option>
          </select>
          <span>{sort ? "Saved as your default" : ""}</span>
        </Flex>

        <TableData
          key={`${JSON.stringify(defaultFilters)}-${tableKey}`}
          returnData={(res: any) => setPageRows(Array.isArray(res?.items) ? res.items : [])}
          api={getLeadsTable}
          stateKey="leads"
          defaultSortBy="nextActionAt"
          defaultSortDir="asc"
          defaultPageSize={25}
          defaultFilters={defaultFilters}
          onFiltersChange={(f: any) => {
            const next: Record<string, string> = {};
            for (const k of ["status", "owner", "source", "dueOnly", "activity", "proposal", "quote", "state"]) if (f?.[k]) next[k] = f[k];
            if (sort) next.sort = sort;
            if (awaitingConvert) next.awaitingConvert = "true";
            setSearchParams(next);
            setStatFilter({ owner: f?.owner || undefined, source: f?.source || undefined });
          }}
          filters={tableFilters}
          nothingFoundText="No leads match. Add one with New lead, or import a list."
          title={awaitingConvert ? "Accepted a quote \u2014 waiting to be converted" : "All leads"}
          rowStyle={(row: LeadRow) =>
            row.nextActionAt && moment(row.nextActionAt).isBefore(moment().startOf("day")) ? { background: "#FFF8F6" } : undefined
          }
          columns={[
            createColumn(
              "select",
              "",
              (row: LeadRow) => row?.id,
              (info: any) => {
                const row: LeadRow = info.row.original;
                return (
                  <input
                    type="checkbox"
                    aria-label={`Select ${row.name}`}
                    checked={!!selected[row.id]}
                    onChange={(e) => toggle(row, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ width: 16, height: 16, cursor: "pointer", accentColor: themevals.colors.black }}
                  />
                );
              },
              {
                enableSorting: false,
                meta: { style: { width: "36px" } },
                header: () => (
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPage}
                    onChange={(e) => pageRows.forEach((r) => toggle(r, e.target.checked))}
                    style={{ width: 16, height: 16, cursor: "pointer", accentColor: themevals.colors.black }}
                  />
                ),
              },
            ),
            createColumn(
              "name",
              "Lead",
              (row: LeadRow) => row?.name,
              (info: any) => {
                const row: LeadRow = info.row.original;
                // Organization, then the PERSON on its own readable line:
                // with several contacts at one org the contact is what tells
                // two rows apart (2026-09-28).
                const detail = [
                  row.contactEmail || row.phone || null,
                  `${row.siteCount} site${row.siteCount === 1 ? "" : "s"}`,
                ].filter(Boolean);
                return (
                  <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/leads/${row.id}`)}>
                    <Box sx={{ color: "#000", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      {info.getValue()}
                      {(row.acceptedQuoteCount ?? 0) > 0 && (
                        <Pill bg="#FBEAEA" fg="#A60000" title="A quote was accepted. Convert this lead so they can log in and pay, and the invoice reaches Zoho Books.">
                          Quote accepted &middot; convert
                        </Pill>
                      )}
                      {row.possibleDuplicateOf && (
                        <span
                          onClick={(e) => { e.stopPropagation(); navigate(`/leads/duplicates?focus=${row.id}`); }}
                          title={`${row.possibleDuplicateOf.reason} - ${row.possibleDuplicateOf.kind} ${row.possibleDuplicateOf.name}. Click to resolve.`}
                        >
                          <Pill bg="#FFF6E5" fg="#8A5A00">Possible duplicate{row.possibleDuplicateOf.kind === "client" ? " of a client" : ""}</Pill>
                        </span>
                      )}
                      {Array.isArray(row.sameOrg) && row.sameOrg.length > 0 && (
                        <OrgContactsMenu orgName={row.name} others={row.sameOrg} size="sm" />
                      )}
                    </Box>
                    <Box sx={{ fontSize: 13, color: "#1a2332", fontWeight: 500, mt: "2px" }}>
                      {row.contactName || <span style={{ color: themevals.colors.dkgrey, fontWeight: 400 }}>no contact yet</span>}
                    </Box>
                    <Box sx={{ fontSize: 11, color: themevals.colors.dkgrey }}>{detail.join(" \u00b7 ")}</Box>
                  </Box>
                );
              },
            ),
            createColumn(
              "leadStatus",
              "Status",
              (row: LeadRow) => row?.leadStatus,
              (info: any) => {
                const s = STATUS_STYLE[info.getValue()] ?? STATUS_STYLE.new;
                return (
                  <Pill bg={s.bg} fg={s.fg}>
                    {LEAD_STATUS_LABELS[info.getValue() as keyof typeof LEAD_STATUS_LABELS] ?? info.getValue()}
                  </Pill>
                );
              },
            ),
            createColumn(
              "owner",
              "Owner",
              (row: LeadRow) => row?.ownerName,
              (info: any) => info.getValue() || <span style={{ color: themevals.colors.dkgrey }}>{"\u2014"}</span>,
            ),
            createColumn(
              "nextActionAt",
              "Next action",
              (row: LeadRow) => row?.nextActionAt,
              (info: any) => {
                const row: LeadRow = info.row.original;
                return (
                  <Box>
                    <NextActionPill at={row.nextActionAt} />
                    {row.nextActionNote && (
                      <Box sx={{ fontSize: 11, color: themevals.colors.black, mt: "3px" }}>{row.nextActionNote}</Box>
                    )}
                  </Box>
                );
              },
            ),
            createColumn(
              "sent",
              "Sent",
              (row: LeadRow) => sentSummary(row),
              (info: any) =>
                info.getValue() ? (
                  <span style={{ fontSize: 12 }}>{info.getValue()}</span>
                ) : (
                  <span style={{ color: themevals.colors.dkgrey }}>{"\u2014"}</span>
                ),
              { enableSorting: false },
            ),
            createColumn(
              "lastActivityAt",
              "Last activity",
              (row: LeadRow) => row?.lastActivityAt,
              (info: any) => {
                const row: LeadRow = info.row.original;
                const at = info.getValue();
                return (
                  <Box>
                    <Box sx={{ fontSize: 12 }}>{at ? relative(at) : "\u2014"}</Box>
                    {row.lastActivitySummary && (
                      <Box sx={{ fontSize: 11, color: themevals.colors.dkgrey }}>{row.lastActivitySummary}</Box>
                    )}
                  </Box>
                );
              },
            ),
          ]}
        />
      </Page>
    </>
  );
};

/** "Yesterday" / "4 days ago" / "Jun 2026" - coarse on purpose. */
export const relative = (at: string) => {
  const d = moment(at);
  const days = moment().startOf("day").diff(d.clone().startOf("day"), "days");
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 14) return `${days} days ago`;
  if (days < 60) return d.format("MMM D");
  return d.format("MMM YYYY");
};

const Stat: React.FC<{ label: string; value: any; tone?: string; onClick?: () => void }> = ({ label, value, tone, onClick }) => (
  <Box
    onClick={onClick}
    sx={{ background: "#F1EFE8", borderRadius: "8px", padding: "14px 16px", cursor: onClick ? "pointer" : undefined, "&:hover": onClick ? { background: "#E8E5DC" } : undefined }}
  >
    <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>{label}</Subtext>
    <Box sx={{ marginTop: "4px" }}>
      <Span fontFamily={"Poppins"} fontWeight={500} fontSize={"22px"} color={tone ?? themevals.colors.black}>{value}</Span>
    </Box>
  </Box>
);

export default LeadsList;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];
